<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { mapsApi } from '$lib/global/maps/api';
    import { cn } from '$lib/utils/cn';

    import {
        createDropdownButtonKeyHandler,
        focusButtonAtIndex,
    } from './dropdownKeyboardNavigation';
    import translations from '../../translations';

    import type { MapsAutoCompleteTypes, Place } from '$lib/global/maps/maps.types';
    import type { Language } from '$lib/types/Language';

    export let type: MapsAutoCompleteTypes;
    export let open = false;
    export let language: Language = 'en';
    export let handleSelect: (place: Place) => void;
    export let filter = '';
    export let apiKey = '';
    export let sessionToken = '';
    export let inputElement: HTMLInputElement | null = null;

    const APIS = {
        fullAddress: mapsApi.fullAddress,
        postal: mapsApi.postals,
        city: mapsApi.cities,
        street: mapsApi.streets,
    };

    let filteredChoices: Place[] = [];
    let dropdownElement: HTMLDivElement;
    let focusedIndex = 0;

    const fetchCities = async (filter: string) => {
        const cities = await APIS[type].search(filter, { apiKey, language, sessionToken });
        filteredChoices = cities;
    };

    $: {
        if (filter !== '') {
            fetchCities(filter);
        }
    }

    $: if (filteredChoices.length > 0 && focusedIndex >= filteredChoices.length) {
        focusedIndex = 0;
    }

    const handleButtonKeyDown = (event: KeyboardEvent, index: number) => {
        if (!open) return;

        const handler = createDropdownButtonKeyHandler(
            {
                currentIndex: index,
                itemCount: filteredChoices.length,
                returnElement: inputElement,
            },
            (newIndex) => {
                focusedIndex = newIndex;

                if (dropdownElement) {
                    focusButtonAtIndex(dropdownElement, newIndex);
                }
            },
        );

        handler(event);
    };

    const borderRadius = getBorderRadius('dropdownItem');
</script>

<div
    class="fun-pointer-events-auto fun-max-h-96 fun-w-full fun-scroll-p-2 fun-overflow-y-auto fun-font-ui"
>
    {#if !!filteredChoices && filteredChoices.length > 0}
        <div class="fun-grid fun-gap-1 fun-p-2" bind:this={dropdownElement}>
            {#each filteredChoices as option, index (option.placeId)}
                <button
                    id={option.placeId}
                    on:click|preventDefault={() => open && handleSelect(option)}
                    on:focus={() => (focusedIndex = index)}
                    on:keydown={(e) => handleButtonKeyDown(e, index)}
                    tabindex={open ? 0 : -1}
                    class={cn(
                        'fun-flex fun-w-full fun-min-w-0 fun-items-center fun-gap-2 fun-px-3 fun-py-2 fun-text-sm fun-font-normal fun-outline-none fun-transition-none sm:hover:fun-bg-gray-100 sm:focus:fun-bg-gray-100 md:fun-text-base',
                        borderRadius,
                    )}
                >
                    <span class="fun-truncate">
                        <span>{option.mainText} - {option.secondaryText}</span>
                    </span>
                </button>
            {/each}
        </div>
    {:else}
        <p class="fun-p-5 fun-text-sm fun-text-gray-500">
            {filter !== ''
                ? translations[language][type].emptyState
                : translations[language][type].emptyFilter}
        </p>
    {/if}
</div>
